import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import type { UseMutationResult, UseQueryResult } from "@tanstack/react-query"
import type { QueryClient } from "@tanstack/react-query"

import { queryKeys } from "~/api/cache"

import type { BatterySupplementalStatusDto } from "../dto/battery-supplemental.dto"
import { batteryProposalService } from "../services/battery-proposal.service"

const applyStatus = (
  queryClient: QueryClient,
  proposalId: string,
  status: BatterySupplementalStatusDto,
): void => {
  queryClient.setQueryData(
    queryKeys.batteryProposal.concertSupplemental(proposalId),
    status,
  )
}

/**
 * The supplemental package for a Concert Battery proposal. Reading is safe:
 * the server sends nothing on a read, and every action it permits comes back
 * in `actions` rather than being inferred here.
 */
export function useBatteryConcertSupplemental(
  proposalId: string | undefined,
): UseQueryResult<BatterySupplementalStatusDto> {
  return useQuery({
    queryKey: queryKeys.batteryProposal.concertSupplemental(proposalId ?? ``),
    queryFn: () => {
      if (!proposalId) {
        throw new Error(`Battery-Only Proposal id is required`)
      }

      return batteryProposalService.getConcertSupplemental(proposalId)
    },
    enabled: Boolean(proposalId),
    retry: 1,
    // A send that just happened is what the seller is looking for, so the
    // drawer always reads through rather than showing a cached "not sent".
    refetchOnMount: `always`,
    refetchOnWindowFocus: false,
  })
}

/** First send for a confirmed package that never had an attempt. */
export function useSendBatteryConcertSupplemental(): UseMutationResult<
  BatterySupplementalStatusDto,
  unknown,
  { proposalId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId }) =>
      batteryProposalService.sendConcertSupplemental(proposalId),
    onSuccess: (status, { proposalId }) =>
      applyStatus(queryClient, proposalId, status),
    onError: (_error, { proposalId }) => {
      // A refused or failed send may still have moved durable state.
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.concertSupplemental(proposalId),
      })
    },
  })
}

export function useRetryBatteryConcertSupplemental(): UseMutationResult<
  BatterySupplementalStatusDto,
  unknown,
  { proposalId: string; attemptId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId, attemptId }) =>
      batteryProposalService.retryConcertSupplemental(proposalId, attemptId),
    onSuccess: (status, { proposalId }) =>
      applyStatus(queryClient, proposalId, status),
    onError: (_error, { proposalId }) => {
      void queryClient.invalidateQueries({
        queryKey: queryKeys.batteryProposal.concertSupplemental(proposalId),
      })
    },
  })
}

export function useResendBatteryConcertSupplemental(): UseMutationResult<
  BatterySupplementalStatusDto,
  unknown,
  { proposalId: string }
> {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ proposalId }) =>
      batteryProposalService.resendConcertSupplemental(proposalId),
    onSuccess: (status, { proposalId }) =>
      applyStatus(queryClient, proposalId, status),
  })
}
